import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { qk } from './queryKeys';
import type {
  AuthResponse, DeviceDto, DeviceRequest, Guid, LoginRequest, MeDto, RefreshRequest, RegisterRequest, UpdateMeRequest,
} from './types';

// ----------------------------------------------------------------------------- endpoints
export const authApi = {
  register: (body: RegisterRequest) => api<AuthResponse>('/api/auth/register', { method: 'POST', body, auth: false }),
  login: (body: LoginRequest) => api<AuthResponse>('/api/auth/login', { method: 'POST', body, auth: false }),
  refresh: (body: RefreshRequest) => api<AuthResponse>('/api/auth/refresh', { method: 'POST', body, auth: false }),
  logout: (body: RefreshRequest) => api<void>('/api/auth/logout', { method: 'POST', body }),
  getMe: () => api<MeDto>('/api/me'),
  updateMe: (body: UpdateMeRequest) => api<MeDto>('/api/me', { method: 'PUT', body }),
  listDevices: () => api<DeviceDto[]>('/api/me/devices'),
  createDevice: (body: DeviceRequest) => api<DeviceDto>('/api/me/devices', { method: 'POST', body }),
  deleteDevice: (deviceId: Guid) => api<void>(`/api/me/devices/${deviceId}`, { method: 'DELETE' }),
};

// ----------------------------------------------------------------------------- hooks
export function useMe(enabled = true) {
  return useQuery({ queryKey: qk.me, queryFn: authApi.getMe, enabled, staleTime: 60_000 });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: authApi.updateMe,
    onSuccess: (me) => qc.setQueryData(qk.me, me),
  });
}

export function useDevices() {
  return useQuery({ queryKey: qk.devices, queryFn: authApi.listDevices });
}

export function useCreateDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: authApi.createDevice,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.devices }),
  });
}

export function useDeleteDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: authApi.deleteDevice,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.devices }),
  });
}
